import React from 'react';
import { useLocation } from 'react-router-dom';

import Image from '../../library/image/image';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';

import {
  useAemContent,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { formatNaDisplayValue } from '../../shared/utils/format/format';

import { ProductData } from '../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';

import './tire-life-estimate-info.scss';

interface TireLifeEstimateInfoProps {
  className?: string;
  median?: number;
  minMileage?: number;
  product?: ProductData;
  showTooltip?: boolean;
}

function TireLifeEstimateInfo(props: TireLifeEstimateInfoProps) {
  const {
    className = '',
    median: medianProp,
    minMileage: minMileageProp,
    product,
    showTooltip = false
  } = props;

  const { mileage } = product?.recommendationData || product?.tdsData || {};
  const { median = medianProp, min: minMileage = minMileageProp } =
    mileage || {};
  const { pathname } = useLocation();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );

  const winterTreadLifeContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.winterTreadlifeEstimate];
  const allSeasonTreadLifeContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.allSeasonTreadlifeEstimate];
  const treadLifeContent =
    treadwellPreferences?.showWinterTires && !pathname.includes('/treadwell')
      ? winterTreadLifeContent
      : allSeasonTreadLifeContent;

  return (
    <div
      className={`${className} tire-life-estimate-info`}
      data-testid="tire-life-estimate-info"
      styleName="container"
    >
      <div styleName="item-container">
        <div styleName="title">
          <Image
            alt="Tread Life Estimate Icon"
            height="22"
            src="https://discounttire.scene7.com/is/content/discounttire/treadwell/treadwell_ic_tread_life_estimate.svg"
            styleName="icon"
            width="22"
          />
          <Typography weight="bold">Tread Life Estimate</Typography>
          {showTooltip && (
            <Tooltip
              content={
                treadLifeContent?.tipText ||
                'Tread life is an estimate based on actual mileage for this tire on your vehicle model, in your area, with proper maintenance.'
              }
              styleName="tooltip"
            />
          )}
        </div>
        <div styleName="estimate-metrics">
          <Typography weight="bold">
            {formatNaDisplayValue(minMileage, true)}
          </Typography>
          <Typography weight="bold">
            {formatNaDisplayValue(median, true)}
          </Typography>
        </div>
        <hr styleName="line-segment" />
        <div styleName="estimate-content">
          <Typography styleName="subtext">infrequent maintenance</Typography>
          <Typography styleName="subtext">proper maintenance</Typography>
        </div>
      </div>
    </div>
  );
}

export default TireLifeEstimateInfo;
